import React, { useEffect, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import moment from "moment";
import { CheckedInUser } from "src/components/CheckedIn/checkedInUser";
import { CheckedInAction } from "src/components/CheckedIn/CheckedInSiteStyles";
import { CheckedInIcon } from "src/components/CheckedIn/CheckedInIcon";
import {
    PostCard, PostCollection, PostContent, PostMedia, ViewerMessage, ViewerRoot, ViewerSection
} from "src/components/CheckedIn/CheckedInContentStyles";
import { surveyTypeOptions } from "../components/Enums";
import { AppSurvey, getActiveSurveys } from "./surveyApi";
import SurveyResponse, { surveyLogo } from "./SurveyResponse";
import {
    CompanyLogo, SurveyCardActions, SurveyConfirmation, SurveyMeta, SurveyTypeIcons
} from "./SurveyViewerStyles";

function SurveyCard({ survey, onStart }: { survey: AppSurvey; onStart: () => void }): React.ReactElement {
    const [imageFailed, setImageFailed] = useState<boolean>(false);
    const typeOption = surveyTypeOptions.find(option => option.value === survey.surveyType);
    const icons: string[] = typeOption ? typeOption.icons || [typeOption.icon] : [];
    return <PostCard aria-label={survey.name}>
        {survey.image.imageUrl && !imageFailed && <PostMedia $detail={false}>
            <img src={survey.image.imageUrl} alt="" loading="lazy"
                style={survey.image.isUsingCompanyLogo ? { objectFit: "contain" } : undefined}
                onError={() => setImageFailed(true)} />
        </PostMedia>}
        <PostContent>
            <h2>{survey.name}</h2>
            <p><time dateTime={survey.finishDate.replace(" ", "T")}>Ends {moment(survey.finishDate,
                "YYYY-MM-DD HH:mm:ss", true).format("MMM Do, YYYY h:mm A")}</time></p>
        </PostContent>
        <SurveyMeta>
            <span>Questions: <strong>{survey.surveyQuestions.length}</strong></span>
            <span>Survey type: <SurveyTypeIcons role="img" aria-label={survey.surveyType}>
                {icons.map((icon, index) => <img src={icon} key={index} alt="" />)}
            </SurveyTypeIcons></span>
        </SurveyMeta>
        <SurveyCardActions><CheckedInAction type="button" $monochrome onClick={onStart}>Start Survey</CheckedInAction></SurveyCardActions>
    </PostCard>;
}

export default function SurveyViewer({ user }: { user: CheckedInUser }): React.ReactElement {
    const history = useHistory();
    const location = useLocation();
    const surveyId: string | null = new URLSearchParams(location.search).get("survey");
    const [surveys, setSurveys] = useState<AppSurvey[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<boolean>(false);
    const [retry, setRetry] = useState<number>(0);
    const [submitted, setSubmitted] = useState<AppSurvey | null>(null);
    const available: AppSurvey[] = surveys.filter(survey => !survey.answered);
    const selected: AppSurvey | undefined = available.find(survey => survey.uuid === surveyId);
    const confirmationLogo: string | null = submitted ? surveyLogo(submitted, user) : null;

    useEffect(() => {
        const controller = new AbortController();
        setLoading(true);
        setError(false);
        void getActiveSurveys(controller.signal).then(data => {
            if (!controller.signal.aborted) {
                setSurveys(data);
                setLoading(false);
            }
        }).catch(() => {
            if (!controller.signal.aborted) {
                setError(true);
                setLoading(false);
            }
        });
        return () => controller.abort();
    }, [retry]);

    useEffect(() => {
        setSubmitted(null);
        window.scrollTo({ top: 0 });
    }, [location.search]);

    const close = (): void => {
        setSubmitted(null);
        history.push("/survey");
    };

    if (submitted) {
        return <SurveyConfirmation>
            <CheckedInAction type="button" $round onClick={close} aria-label="Close confirmation">
                <CheckedInIcon name="close" /></CheckedInAction>
            {confirmationLogo && <CompanyLogo src={confirmationLogo} alt={user.companyName || "Company logo"} />}
            <h1>Thank you for your feedback!</h1>
            <p>We sincerely appreciate you taking the time to complete this survey. Your insights and honest opinions
                are invaluable to us, and we&apos;re grateful for your contribution!</p>
        </SurveyConfirmation>;
    }

    return <ViewerRoot>
        {loading ? <ViewerMessage role="status">Loading surveys…</ViewerMessage> : error ? (
            <ViewerMessage $error role="alert">We couldn’t load your surveys. Please try again.
                <div><CheckedInAction type="button" $outline onClick={() => setRetry(value => value + 1)}>Try again</CheckedInAction></div>
            </ViewerMessage>
        ) : surveyId ? selected ? (
            <SurveyResponse key={selected.uuid} survey={selected} user={user} onClose={close} onSubmitted={() => {
                setSubmitted(selected);
                setSurveys(current => current.map(survey => survey.uuid === selected.uuid ? { ...survey, answered: true } : survey));
                window.scrollTo({ top: 0 });
            }} />
        ) : <ViewerMessage role="status">This survey is no longer available or has already been submitted.
            <div><CheckedInAction type="button" onClick={close}>Back to Surveys</CheckedInAction></div>
        </ViewerMessage> : <>
            <ViewerSection>
                <h2>{available.length} Available {available.length === 1 ? "Survey" : "Surveys"}</h2>
                <p>Participate in surveys tailored for you. Share your feedback and insights to help shape future decisions.</p>
                {available.length > 0 ? <PostCollection $horizontal={false} aria-label="Available surveys">
                    {available.map(survey => <SurveyCard key={survey.uuid} survey={survey}
                        onStart={() => history.push(`/survey?survey=${encodeURIComponent(survey.uuid)}`)} />)}
                </PostCollection> : <ViewerMessage role="status">You&apos;re all caught up. There are no surveys available right now.</ViewerMessage>}
            </ViewerSection>
        </>}
    </ViewerRoot>;
}
